import { useState, memo, Suspense, useEffect, FC } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import tradingModalStore from 'global/TradingModal/store';
import { iPreviewData } from 'typings/transactionForm';

import tradingDataStore from 'lib/stores/tradingData';
import dynamic from 'next/dynamic';
import { BuySellComponent } from 'buysell/form';
import { WidgetManagerProvider } from 'buysell/widget-manager-provider';
import { ThemeMode } from 'typings/theme';
import moment from 'moment';
import {
  SubAccountType,
  TnCFeatureIDEnum,
  TnCFeaturesIDEnum,
} from 'global/Securities/types/termsAndCondition';
import useTermsAndConditionStore from 'global/Securities/stores/useTermsAndConditionStore';
import useUIConfigStore from 'stores/useUIConfigStore';
import { PaymentMethod } from 'global/Securities/types/transaction';
import { ORDER_TYPE } from 'constants/trading';
import {
  iFormData,
  MODAL_TYPE,
} from 'global/TradingModal/RealTradingModal/types';
import useSyariah from 'global/TradingModal/hooks/useSyariah';
import useKeyPress from 'hooks/useKeyPress';
import { PROJECT_FOLDER_PATH } from 'constants/app';
import useCashInfoStore from 'global/Securities/stores/useCashInfoStore';
import { useFliptBoolean } from 'lib/flipt/hooks';
import { FeatureFlagKeys } from 'lib/flipt/constants/flagKeys';
import useIsSecuritiesMainAccount from 'hooks/useIsSecuritiesMainAccount';
import TradingFormSkeleton from './Skeleton';
import useTradingStore from './useTradingStore';
import { useBuySellDatafeed } from './useBuySellDatafeed';

const BuySellRemote = dynamic(() => import('buysell/form'), {
  ssr: false,
  loading: () => <TradingFormSkeleton />,
}) as BuySellComponent;

const WidgetManager = dynamic(() => import('buysell/widget-manager-provider'), {
  ssr: false,
  loading: () => <TradingFormSkeleton />,
}) as WidgetManagerProvider;

const ModuleFederationForm: FC<{
  formId: string;
  initialData: iFormData;
  customPrice?: number;
  onSwitchTab?: () => void;
}> = ({ initialData, formId, customPrice, onSwitchTab }) => {
  useBuySellDatafeed(initialData.currentSymbol);

  const queryClient = useQueryClient();

  const isDayTradeEnabled = true;
  const isMarketOrderEnabled = true;

  const isTrailingStopEnabled = useFliptBoolean(FeatureFlagKeys.TRAILING_STOP);
  const isBracketOrderEnabled = useFliptBoolean(FeatureFlagKeys.BRACKET_ORDER);

  const setUIValue = useUIConfigStore((s) => s.setUIValue);
  const getUIValue = useUIConfigStore((s) => s.getUIValue);
  const orderType = tradingModalStore((s) => s.orderType);
  const isGtc = tradingModalStore((s) => s.isGtc);
  const setModal = useTradingStore((s) => s.setModal);
  const openFromWidget = useTradingStore((s) => s.openFromWidget);
  const loadCashInfo = useCashInfoStore((s) => s.loadCashInfo);
  const { isUserSyariah: isSyariahUser, checkSyariahTradeableStatus } =
    useSyariah();
  const orderList = tradingDataStore((s) => s.orderHistory);
  const isMainAccount = useIsSecuritiesMainAccount();

  const showTradingLimit = isMainAccount;

  const loadTnC = useTermsAndConditionStore((s) => s.loadTnC);
  const termsData = useTermsAndConditionStore((s) => s.termsData);
  const loadTnCAcceptance = useTermsAndConditionStore(
    (s) => s.loadTnCAcceptance,
  );
  const loadSubAccountStatus = useTermsAndConditionStore(
    (s) => s.loadSubAccountStatus,
  );

  const [portoUpdateTime, setPortoUpdateTime] = useState<string | null>(null);

  useEffect(() => {
    if (orderList) {
      const currentTime = moment().format('YYYYMMDDHHmmssSSS');
      setPortoUpdateTime(currentTime);
    }
  }, [orderList]);

  const loadDayTradeTncStatus = async () =>
    loadTnCAcceptance([
      TnCFeaturesIDEnum.TRADING_LIMIT,
      TnCFeaturesIDEnum.DAY_TRADE,
    ]);

  const loadDayTradeSubAccountStatus = async () => {
    const subAccountActive = getUIValue('dayTradeSubAccountActive');
    if (subAccountActive) return subAccountActive;

    return loadSubAccountStatus(SubAccountType.DAY_TRADE);
  };

  const getDayTradeTncStatus = async (checkShown = true) => {
    if (isSyariahUser) return true;

    if (!termsData) await loadDayTradeTncStatus();

    const isActive = await loadDayTradeSubAccountStatus();
    if (isActive) return true;

    const dayTradeTncShown = getUIValue('dayTradeTncShown');
    if (dayTradeTncShown !== undefined && checkShown) return dayTradeTncShown;

    const dayTradeTnc = getUIValue('dayTradeTnc');
    if (dayTradeTnc !== undefined) return dayTradeTnc;

    return loadDayTradeTncStatus();
  };

  const handleOpenModalDayTrade = async (
    open: boolean,
    callback: () => void,
  ) => {
    if (open) {
      const isDayTradeAccepted = await getDayTradeTncStatus(false);
      if (!isDayTradeAccepted) {
        setModal(MODAL_TYPE.DAY_TRADE_TNC, {
          onSubmitDayTradeTnC: () => {
            callback();
            setModal('', { onSubmitDayTradeTnC: undefined });
          },
        });
      }
    }
  };

  const handleShowTradingLimitTnc = (open: boolean, callback: () => void) => {
    if (open) {
      loadTnC(
        TnCFeatureIDEnum.FEATURE_TRADING_LIMIT,
        /**
         * If user is not yet accepted the TnC,
         * then show the modal using the callback
         */
        () => {
          setUIValue('tradingLimitTnc', false);
          setUIValue('transactionPaymentMethod', PaymentMethod.TradingBalance);
          setModal(MODAL_TYPE.TRADING_LIMIT_TNC);

          setModal(MODAL_TYPE.TRADING_LIMIT_TNC, {
            onSubmitTradingLimitTnC: () => {
              callback();
              setModal('', { onSubmitTradingLimitTnC: undefined });
            },
          });
        },
        /**
         * If user was accepted the TnC before,
         * then show continue their action to use the Trading limit as payment
         */
        () => {
          setUIValue('tradingLimitTnc', true);
          setUIValue('transactionPaymentMethod', PaymentMethod.TradingLimit);
          if (!openFromWidget) setModal(MODAL_TYPE.TRANSACTION);
        },
      );
    }
  };

  const getShariaTradableStatus = async () => {
    const isBuy = orderType === ORDER_TYPE.BUY;
    if (!isSyariahUser || !isBuy) return true;

    const isShariaTradable = await checkSyariahTradeableStatus(
      initialData.currentSymbol,
    );
    if (!isShariaTradable) {
      setModal(MODAL_TYPE.SHARIA_UNAVAILABLE);
      return false;
    }

    return true;
  };

  const handlePlaceOrder = async (data: iPreviewData) => {
    if (!(await getShariaTradableStatus())) return;

    setModal(MODAL_TYPE.PREVIEW, {
      previewData: {
        ...data,
        ...(isGtc !== undefined ? { gtc: isGtc } : {}),
      },
    });
  };

  useKeyPress(['Enter'], () => {
    const customEvent = new CustomEvent('host-buysell', {
      detail: { name: 'submit-order', id: formId },
    });
    window.dispatchEvent(customEvent);
  });

  useEffect(() => {
    if (
      initialData.currentSymbol &&
      [ORDER_TYPE.BUY, ORDER_TYPE.AMEND_BUY].includes(orderType)
    ) {
      loadCashInfo(initialData.currentSymbol);
    }
  }, [initialData.currentSymbol]);

  return (
    <Suspense fallback={<TradingFormSkeleton />}>
      <div style={{ margin: '0 -12px', flex: 1, minHeight: 0 }}>
        <WidgetManager value={{ channel: 0, tabId: '', widgetId: '' }}>
          <BuySellRemote
            formId={formId}
            themeMode={ThemeMode.Light}
            platform="web"
            queryClient={queryClient}
            initalFormData={initialData}
            showIcon
            showTradingLimit={showTradingLimit}
            enabledDayTrade={isDayTradeEnabled}
            isMainAccount={isMainAccount}
            openDeposit={() =>
              window.open(
                `${window.location.origin}${PROJECT_FOLDER_PATH}/securities/deposit`,
              )
            }
            openPreview={handlePlaceOrder}
            marketEnabled={isMarketOrderEnabled}
            isSyariahUser={isSyariahUser}
            openDayTradeTnC={handleOpenModalDayTrade}
            openTradingLimitTnC={handleShowTradingLimitTnc}
            portoUpdateTime={portoUpdateTime}
            useEnterToSubmit={false}
            isActive
            portalId={openFromWidget ? undefined : 'buy-sell-portal'}
            isOrderOnProcess={false}
            disableInitialAutofocus={openFromWidget}
            customPrice={customPrice}
            onUpdateFormType={onSwitchTab}
            featureFlags={{
              trailingStopEnabled: isTrailingStopEnabled,
              bracketOrderEnabled: isBracketOrderEnabled,
            }}
          />
        </WidgetManager>
      </div>
    </Suspense>
  );
};

export default memo(ModuleFederationForm);
